/**
 * ScheduleWidget
 * Standalone embeddable homepage/theodds schedule explorer.
 * Owns sport pills, league dropdowns, schedule fetching, and event rendering.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { AlertCircle, Calendar, ChevronDown, ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import { fetchOddsEvents, fetchOddsScores, fetchOddsSports } from '../lib/api'
import { getLeagueLogoPath, getTeamLogoPath } from '../lib/teamLogos'
import './ScheduleWidget.css'

const DYNAMIC_GROUP_FALLBACKS = {
  Soccer: [
    { id: 'soccer_epl', label: 'EPL', logo: null },
    { id: 'soccer_spain_la_liga', label: 'La Liga', logo: null },
    { id: 'soccer_germany_bundesliga', label: 'Bundesliga', logo: null },
    { id: 'soccer_italy_serie_a', label: 'Serie A', logo: null },
    { id: 'soccer_france_ligue_one', label: 'Ligue 1', logo: null },
    { id: 'soccer_usa_mls', label: 'MLS', logo: null },
    { id: 'soccer_uefa_champs_league', label: 'UCL', logo: null },
  ],
  Tennis: [
    { id: 'tennis_wta_charleston_open', label: 'Charleston', logo: '/logos/leagues/TENNIS.svg' },
  ],
  Golf: [
    { id: 'golf_masters_tournament_winner', label: 'Masters', logo: null },
    { id: 'golf_pga_championship_winner', label: 'PGA', logo: null },
    { id: 'golf_the_open_championship_winner', label: 'Open', logo: null },
    { id: 'golf_us_open_winner', label: 'US Open', logo: null },
  ],
  Combat: [
    { id: 'mma_mixed_martial_arts', label: 'MMA', logo: '/logos/leagues/MMA.svg' },
    { id: 'boxing_boxing', label: 'Boxing', logo: '/logos/leagues/BOXING.svg' },
  ],
}

const BASE_SPORT_BUTTONS = [
  { id: 'baseball_mlb', label: 'MLB', logo: '/logos/leagues/MLB.png' },
  { id: 'basketball_nba', label: 'NBA', logo: '/logos/leagues/NBA.png' },
  { id: 'icehockey_nhl', label: 'NHL', logo: '/logos/leagues/NHL.png' },
  { id: 'americanfootball_nfl', label: 'NFL', logo: '/logos/leagues/NFL.svg' },
  { id: 'americanfootball_ncaaf', label: 'CFB', logo: '/logos/leagues/CFB.svg' },
  {
    id: 'basketball',
    label: 'Basketball',
    logo: '/logos/leagues/CBB.svg',
    leagues: [
      { id: 'basketball_ncaab', label: "Men's CBB", logo: '/logos/leagues/CBB.svg' },
      { id: 'basketball_wncaab', label: "Women's CBB", logo: '/logos/leagues/CBB.svg' },
      { id: 'basketball_wnba', label: 'WNBA', logo: '/logos/leagues/WNBA.svg' },
    ],
  },
  {
    id: 'soccer',
    label: 'Soccer',
    logo: '/logos/leagues/SOCCER.svg',
    dynamicGroup: 'Soccer',
    leagues: DYNAMIC_GROUP_FALLBACKS.Soccer,
  },
  {
    id: 'tennis',
    label: 'Tennis',
    logo: '/logos/leagues/TENNIS.svg',
    dynamicGroup: 'Tennis',
    leagues: DYNAMIC_GROUP_FALLBACKS.Tennis,
  },
  {
    id: 'golf',
    label: 'Golf',
    logo: null,
    dynamicGroup: 'Golf',
    leagues: DYNAMIC_GROUP_FALLBACKS.Golf,
  },
  {
    id: 'combat',
    label: 'Combat',
    logo: '/logos/leagues/MMA.svg',
    dynamicGroup: 'Combat',
    leagues: DYNAMIC_GROUP_FALLBACKS.Combat,
  },
]

const MLB_LIVE_STATUS_CODES = new Set(['I', 'IR', 'MA', 'MI'])
const MLB_FINAL_STATUS_CODES = new Set(['F', 'FT', 'FR', 'FO', 'CR', 'GO'])
const MLB_POSTPONED_STATUS_CODES = new Set(['D', 'DI', 'DR'])
const SCORE_ENDPOINT_EXCLUDED_PREFIXES = ['golf_']

function normalizeComparableText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '')
    .toLowerCase()
}

function buildInitials(label) {
  const parts = String(label || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
}

function resolveScheduleLeagueLogo(sportKey) {
  if (sportKey === 'basketball_wnba') return '/logos/leagues/WNBA.svg'
  if (sportKey === 'boxing_boxing') return '/logos/leagues/BOXING.svg'
  return getLeagueLogoPath(sportKey)
}

function formatDateYMD(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatEventDateLabel(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function formatEventTimeLabel(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function buildBaseState(offense) {
  if (!offense) return { label: 'Bases empty', occupied: 0 }
  const occupied = []
  if (offense.first) occupied.push('1B')
  if (offense.second) occupied.push('2B')
  if (offense.third) occupied.push('3B')
  return {
    label: occupied.length ? `On ${occupied.join(', ')}` : 'Bases empty',
    occupied: occupied.length,
  }
}

async function fetchMLBHomepageDate(dateStr) {
  const response = await fetch(
    `https://statsapi.mlb.com/api/v1/schedule?sportId=1&date=${dateStr}&hydrate=linescore`,
    { signal: AbortSignal.timeout(10000) },
  )
  if (!response.ok) throw new Error(`MLB API ${response.status}`)
  const payload = await response.json()

  const games = []
  for (const dateEntry of payload.dates || []) {
    for (const game of dateEntry.games || []) {
      const away = game.teams?.away
      const home = game.teams?.home
      const linescore = game.linescore || {}
      games.push({
        id: `mlb-${game.gamePk}`,
        source: 'mlb-stats',
        sport_title: 'MLB',
        commence_time: game.gameDate,
        away_team: away?.team?.name || 'TBD',
        home_team: home?.team?.name || 'TBD',
        away_score: away?.score ?? null,
        home_score: home?.score ?? null,
        status_code: game.status?.statusCode || 'S',
        status_text: game.status?.detailedState || game.status?.abstractGameState || 'Scheduled',
        is_live: MLB_LIVE_STATUS_CODES.has(game.status?.statusCode),
        is_final: MLB_FINAL_STATUS_CODES.has(game.status?.statusCode),
        is_postponed: MLB_POSTPONED_STATUS_CODES.has(game.status?.statusCode),
        inning: linescore.currentInning || null,
        inning_half: linescore.inningHalf || null,
        outs: Number.isFinite(linescore.outs) ? linescore.outs : null,
        base_state: buildBaseState(linescore.offense),
        series_description: game.seriesDescription || '',
      })
    }
  }

  return games
}

async function fetchMLBHomepageSchedule() {
  const dates = []
  for (let i = 0; i < 4; i += 1) {
    const date = new Date()
    date.setDate(date.getDate() + i)
    dates.push(formatDateYMD(date))
  }

  const results = await Promise.all(dates.map((dateStr) => fetchMLBHomepageDate(dateStr).catch(() => [])))
  return results.flat().sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
}

function getEventStatusMeta(event) {
  if (event?.source === 'mlb-stats') {
    if (event.is_live) {
      const inningLabel = event.inning
        ? `${event.inning_half === 'Top' ? 'Top' : 'Bot'} ${event.inning}`
        : 'Live'
      const outsLabel = event.outs == null ? null : `${event.outs} out${event.outs === 1 ? '' : 's'}`
      return {
        badge: 'Live',
        badgeClass: 'live',
        detail: inningLabel,
        extra: [outsLabel, event.base_state?.label].filter(Boolean).join(' • '),
      }
    }

    if (event.is_final) {
      return {
        badge: 'Final',
        badgeClass: 'final',
        detail: event.status_text || 'Final',
        extra: event.series_description || '',
      }
    }

    if (event.is_postponed) {
      return {
        badge: 'PPD',
        badgeClass: 'postponed',
        detail: event.status_text || 'Postponed',
        extra: event.series_description || '',
      }
    }

    return {
      badge: 'Scheduled',
      badgeClass: 'scheduled',
      detail: '',
      extra: event.series_description || '',
    }
  }

  if (event?.completed || event?.is_final) {
    return {
      badge: 'Final',
      badgeClass: 'final',
      detail: event.last_update ? `Updated ${formatEventTimeLabel(event.last_update)}` : '',
      extra: '',
    }
  }

  if (event?.is_live) {
    return {
      badge: 'Live',
      badgeClass: 'live',
      detail: event.last_update ? `Updated ${formatEventTimeLabel(event.last_update)}` : 'In progress',
      extra: '',
    }
  }

  return {
    badge: 'Scheduled',
    badgeClass: 'scheduled',
    detail: '',
    extra: '',
  }
}

function buildDynamicLeagueOptions(sports = [], groupName) {
  const matched = sports
    .filter((sport) => {
      if (!sport?.active) return false
      if (groupName === 'Soccer') return sport.key?.startsWith('soccer_') || sport.group === 'Soccer'
      if (groupName === 'Tennis') return sport.key?.startsWith('tennis_') || sport.group === 'Tennis'
      if (groupName === 'Golf') return sport.key?.startsWith('golf_') || sport.group === 'Golf'
      if (groupName === 'Combat') return sport.key?.startsWith('mma_') || sport.key?.startsWith('boxing_') || sport.group === 'Mixed Martial Arts' || sport.group === 'Boxing'
      return false
    })
    .map((sport) => ({
      id: sport.key,
      label: sport.title
        .replace(/\s+Winner$/i, '')
        .replace(/^WTA\s+/i, '')
        .replace(/^ATP\s+/i, '')
        .replace(/\s+Tournament$/i, '')
        .trim(),
      logo: groupName === 'Combat'
        ? sport.key?.startsWith('boxing_')
          ? '/logos/leagues/BOXING.svg'
          : '/logos/leagues/MMA.svg'
        : resolveScheduleLeagueLogo(sport.key),
    }))

  return matched.length ? matched : (DYNAMIC_GROUP_FALLBACKS[groupName] || [])
}

function resolveActiveLabel(activeSport, sportButtons) {
  for (const button of sportButtons) {
    if (button.id === activeSport) return button.label
    if (button.leagues) {
      const league = button.leagues.find((entry) => entry.id === activeSport)
      if (league) return league.label
    }
  }
  return activeSport
}

function isButtonActive(button, activeSport) {
  return button.id === activeSport || Boolean(button.leagues?.some((league) => league.id === activeSport))
}

function extractTeamScore(event, teamName) {
  if (!Array.isArray(event?.scores) || !teamName) return null
  const target = normalizeComparableText(teamName)
  const found = event.scores.find((entry) => normalizeComparableText(entry?.name) === target)
  return found?.score ?? null
}

function normalizeOddsEvent(event) {
  return {
    ...event,
    source: 'theodds-events',
    away_score: null,
    home_score: null,
    is_live: false,
    is_final: false,
  }
}

function normalizeOddsScoreEvent(event) {
  const commenceTs = Date.parse(event?.commence_time || '')
  const hasBegun = Number.isFinite(commenceTs) ? commenceTs <= Date.now() : false
  const awayScore = extractTeamScore(event, event?.away_team)
  const homeScore = extractTeamScore(event, event?.home_team)
  const hasScores = awayScore != null || homeScore != null

  return {
    ...event,
    source: 'theodds-scores',
    away_score: awayScore,
    home_score: homeScore,
    is_live: Boolean(!event?.completed && (hasScores || (event?.last_update && hasBegun))),
    is_final: Boolean(event?.completed),
    status_text: event?.completed ? 'Complete' : (hasBegun ? 'In progress' : 'Scheduled'),
  }
}

function eventSortRank(event) {
  if (event?.is_live) return 0
  if (!event?.is_final) return 1
  return 2
}

function sortScheduleEvents(a, b) {
  const rankDelta = eventSortRank(a) - eventSortRank(b)
  if (rankDelta !== 0) return rankDelta

  const aTime = Date.parse(a?.commence_time || '') || 0
  const bTime = Date.parse(b?.commence_time || '') || 0
  if (eventSortRank(a) === 2) return bTime - aTime
  return aTime - bTime
}

function renderCompetitorVisual(name, sportKey) {
  const logoPath = getTeamLogoPath(name, sportKey)
  if (logoPath) {
    return (
      <span className="schedule-event-logo-shell">
        <img
          src={logoPath}
          alt=""
          className="schedule-event-logo"
          onError={(err) => {
            err.currentTarget.parentElement.style.display = 'none'
          }}
        />
      </span>
    )
  }

  return (
    <span className="schedule-event-avatar" aria-hidden="true">
      {buildInitials(name)}
    </span>
  )
}

export default function ScheduleWidget({
  navigateTo,
  onEventSelect,
  onModuleSelect,
  onSportSelect,
  sportFilter = null,
  variant = 'default',
}) {
  const initialSport = sportFilter || 'baseball_mlb'
  const [activeSport, setActiveSport] = useState(initialSport)
  const [dropdownState, setDropdownState] = useState(null)
  const [sportButtons, setSportButtons] = useState(BASE_SPORT_BUTTONS)
  const [events, setEvents] = useState([])
  const [eventCount, setEventCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const widgetRef = useRef(null)
  const scrollRef = useRef(null)
  const dropdownRef = useRef(null)
  const cacheRef = useRef({})

  useEffect(() => {
    if (sportFilter) {
      setActiveSport(sportFilter)
    }
  }, [sportFilter])

  useEffect(() => {
    let cancelled = false

    async function hydrateSportButtons() {
      const response = await fetchOddsSports()
      if (cancelled || response.status !== 'ok' || !Array.isArray(response.data)) return

      const nextButtons = BASE_SPORT_BUTTONS.map((sport) => {
        if (!sport.dynamicGroup) return sport
        return {
          ...sport,
          leagues: buildDynamicLeagueOptions(response.data, sport.dynamicGroup),
        }
      })

      setSportButtons(nextButtons)
    }

    hydrateSportButtons()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchEvents = useCallback(async (sportKey) => {
    const cached = cacheRef.current[sportKey]
    if (cached && Date.now() - cached.ts < 300000) {
      setEvents(cached.data)
      setEventCount(cached.data.length)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = sportKey === 'baseball_mlb'
        ? await fetchMLBHomepageSchedule()
        : await (async () => {
            const useScoresEndpoint = !SCORE_ENDPOINT_EXCLUDED_PREFIXES.some((prefix) => sportKey.startsWith(prefix))
            const [eventsResponse, scoresResponse] = await Promise.all([
              fetchOddsEvents(sportKey),
              useScoresEndpoint
                ? fetchOddsScores(sportKey, { daysFrom: 3 })
                : Promise.resolve({ status: 'ok', data: [] }),
            ])

            const merged = new Map()
            const errors = []

            if (scoresResponse.status === 'ok' && Array.isArray(scoresResponse.data)) {
              scoresResponse.data.forEach((event) => {
                const normalized = normalizeOddsScoreEvent(event)
                if (normalized?.id) merged.set(normalized.id, normalized)
              })
            } else if (useScoresEndpoint && scoresResponse.message) {
              errors.push(scoresResponse.message)
            }

            if (eventsResponse.status === 'ok' && Array.isArray(eventsResponse.data)) {
              eventsResponse.data.forEach((event) => {
                const normalized = normalizeOddsEvent(event)
                if (normalized?.id && !merged.has(normalized.id)) {
                  merged.set(normalized.id, normalized)
                }
              })
            } else if (eventsResponse.message) {
              errors.push(eventsResponse.message)
            }

            if (!merged.size && errors.length) {
              throw new Error(errors[0])
            }

            return [...merged.values()]
          })()

      const normalized = Array.isArray(data)
        ? [...data].sort(sortScheduleEvents)
        : []

      cacheRef.current[sportKey] = { data: normalized, ts: Date.now() }
      setEvents(normalized)
      setEventCount(normalized.length)
    } catch (err) {
      setEvents([])
      setEventCount(0)
      setError(err.message || 'Failed to load schedule')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents(activeSport)
  }, [activeSport, fetchEvents])

  useEffect(() => {
    const handleOutsidePointer = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setDropdownState(null)
      }
    }

    document.addEventListener('mousedown', handleOutsidePointer)
    document.addEventListener('touchstart', handleOutsidePointer, { passive: true })

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer)
      document.removeEventListener('touchstart', handleOutsidePointer)
    }
  }, [])

  useEffect(() => {
    const closeDropdown = () => setDropdownState(null)
    const scroller = scrollRef.current

    window.addEventListener('resize', closeDropdown)
    scroller?.addEventListener('scroll', closeDropdown, { passive: true })

    return () => {
      window.removeEventListener('resize', closeDropdown)
      scroller?.removeEventListener('scroll', closeDropdown)
    }
  }, [])

  const openDropdown = useCallback((sport, button) => {
    const rect = button.getBoundingClientRect()
    const minWidth = Math.max(196, Math.ceil(rect.width))
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0
    let left = rect.left

    if (left + minWidth > viewportWidth - 12) {
      left = Math.max(12, viewportWidth - minWidth - 12)
    }

    setDropdownState({
      sportId: sport.id,
      top: rect.bottom + 6,
      left,
      minWidth,
    })
  }, [])

  const handleShowAll = useCallback(() => {
    if (navigateTo) {
      navigateTo('theodds', 'events-browser')
      return
    }

    if (onSportSelect) {
      onSportSelect(activeSport)
    }
    if (onModuleSelect) {
      onModuleSelect('theodds')
    }
  }, [activeSport, navigateTo, onModuleSelect, onSportSelect])

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction * 120, behavior: 'smooth' })
    }
  }

  const activeLabel = resolveActiveLabel(activeSport, sportButtons)
  const openSport = sportButtons.find((sport) => sport.id === dropdownState?.sportId) || null
  const shellClassName = variant === 'homepage'
    ? 'schedule-widget schedule-widget-homepage'
    : 'schedule-widget'

  return (
    <section className={shellClassName} ref={widgetRef}>
      <div className="schedule-bar">
        <div className="schedule-title-row">
          <Calendar size={16} />
          <span className="schedule-title">{activeLabel} Schedule</span>
          {eventCount > 0 && <span className="schedule-count">{eventCount} events</span>}
        </div>
        <button className="schedule-more" onClick={handleShowAll}>
          All Events <ChevronRight size={12} />
        </button>
      </div>

      {!sportFilter && (
        <div className="schedule-pill-row">
          <button className="schedule-pill-scroll-btn" onClick={() => scroll(-1)} aria-label="Scroll sports left">
            <ChevronLeft size={14} />
          </button>
          <div className="schedule-pill-scroll" ref={scrollRef}>
            {sportButtons.map((sport) => (
              <div key={sport.id} className="schedule-pill-wrap">
                <button
                  className={`schedule-pill ${isButtonActive(sport, activeSport) ? 'active' : ''}`}
                  onClick={(event) => {
                    if (sport.leagues) {
                      if (dropdownState?.sportId === sport.id) {
                        setDropdownState(null)
                      } else {
                        openDropdown(sport, event.currentTarget)
                      }
                      return
                    }

                    setActiveSport(sport.id)
                    setDropdownState(null)
                  }}
                >
                  {(sport.logo || getLeagueLogoPath(sport.id)) ? (
                    <img
                      src={sport.logo || getLeagueLogoPath(sport.id)}
                      alt={sport.label}
                      className="schedule-pill-logo"
                      onError={(event) => {
                        event.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : null}
                  <span className="schedule-pill-label">{sport.label}</span>
                  {sport.leagues ? <ChevronDown size={10} className="schedule-pill-dd-icon" /> : null}
                </button>
              </div>
            ))}
          </div>
          <button className="schedule-pill-scroll-btn" onClick={() => scroll(1)} aria-label="Scroll sports right">
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {openSport?.leagues && dropdownState ? (
        <div
          ref={dropdownRef}
          className="schedule-dropdown"
          style={{
            top: `${dropdownState.top}px`,
            left: `${dropdownState.left}px`,
            minWidth: `${dropdownState.minWidth}px`,
          }}
        >
          {openSport.leagues.map((league) => (
            <button
              key={league.id}
              className={`schedule-dd-item ${activeSport === league.id ? 'active' : ''}`}
              onClick={() => {
                setActiveSport(league.id)
                setDropdownState(null)
              }}
            >
              {league.logo ? (
                <img
                  src={league.logo}
                  alt={league.label}
                  className="schedule-dd-logo"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none'
                  }}
                />
              ) : null}
              <span>{league.label}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="schedule-content">
        {loading ? (
          <div className="schedule-placeholder">
            <Calendar size={24} className="schedule-placeholder-icon" />
            <span>Loading {activeLabel} schedule...</span>
          </div>
        ) : error ? (
          <div className="schedule-placeholder">
            <AlertCircle size={24} className="schedule-placeholder-error" />
            <span className="schedule-error-text">{error}</span>
            <button className="schedule-retry" onClick={() => fetchEvents(activeSport)}>Retry</button>
          </div>
        ) : events.length === 0 ? (
          <div className="schedule-placeholder">
            <Calendar size={24} className="schedule-placeholder-icon" />
            <span>No upcoming {activeLabel} events found</span>
            <span className="schedule-placeholder-sub">Check back closer to game day or try another sport</span>
          </div>
        ) : (
          <div className="schedule-list">
            {events.slice(0, 20).map((event) => {
              const statusMeta = getEventStatusMeta(event)
              const competitionLabel = event?.sport_title || activeLabel
              const leagueLogo = resolveScheduleLeagueLogo(event?.sport_key || activeSport)
              const eventIsInteractive = Boolean(onEventSelect && event.away_team && event.home_team)
              const EventElement = eventIsInteractive ? 'button' : 'div'
              return (
                <EventElement
                  key={event.id}
                  {...(eventIsInteractive ? {
                    type: 'button',
                    onClick: () => onEventSelect(event, event?.sport_key || activeSport),
                    title: `Open ${event.away_team} at ${event.home_team} event intelligence`,
                  } : {})}
                  className={`schedule-event ${eventIsInteractive ? 'schedule-event-interactive' : ''} ${!event.away_team && !event.home_team ? 'schedule-event-outright' : ''}`}
                >
                  <div className="schedule-event-meta">
                    <span className="schedule-event-league">
                      {leagueLogo ? (
                        <img
                          src={leagueLogo}
                          alt=""
                          className="schedule-event-league-logo"
                          onError={(err) => {
                            err.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : null}
                      <span>{competitionLabel}</span>
                    </span>
                    <span className="schedule-event-time-top">{formatEventDateLabel(event.commence_time)}</span>
                    <span className="schedule-event-time-bottom">
                      <Clock size={11} />
                      <span>{formatEventTimeLabel(event.commence_time)}</span>
                    </span>
                  </div>

                  {event.away_team || event.home_team ? (
                    <>
                      <div className="schedule-event-matchup">
                        <div className="schedule-event-team-row schedule-event-away">
                          {renderCompetitorVisual(event.away_team, activeSport)}
                          <span className="schedule-event-team-name">{event.away_team}</span>
                          <span className="schedule-event-team-connector">@</span>
                        </div>
                        <div className="schedule-event-team-row schedule-event-home">
                          {renderCompetitorVisual(event.home_team, activeSport)}
                          <span className="schedule-event-team-name">{event.home_team}</span>
                          <span className="schedule-event-team-connector" aria-hidden="true">&nbsp;</span>
                        </div>
                      </div>
                      <div className="schedule-event-scoreboard">
                        <span className={`schedule-event-score ${event.away_score == null ? 'pending' : ''}`}>{event.away_score ?? '—'}</span>
                        <span className={`schedule-event-score ${event.home_score == null ? 'pending' : ''}`}>{event.home_score ?? '—'}</span>
                      </div>
                      <div className="schedule-event-state">
                        <span className={`schedule-event-badge schedule-event-badge-${statusMeta.badgeClass}`}>{statusMeta.badge}</span>
                        {statusMeta.detail ? <span className="schedule-event-state-detail">{statusMeta.detail}</span> : null}
                        {statusMeta.extra ? <span className="schedule-event-state-extra">{statusMeta.extra}</span> : null}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="schedule-outright-card">
                        <span className="schedule-outright-title">{event.sport_title || activeLabel}</span>
                        <span className="schedule-outright-subtitle">{activeLabel}</span>
                      </div>
                      <div className="schedule-event-state">
                        <span className={`schedule-event-badge schedule-event-badge-${statusMeta.badgeClass}`}>{statusMeta.badge}</span>
                        {statusMeta.detail ? <span className="schedule-event-state-detail">{statusMeta.detail}</span> : null}
                        {statusMeta.extra ? <span className="schedule-event-state-extra">{statusMeta.extra}</span> : null}
                      </div>
                    </>
                  )}
                </EventElement>
              )
            })}

            {events.length > 20 ? (
              <button className="schedule-show-all" onClick={handleShowAll}>
                +{events.length - 20} more events →
              </button>
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}
